/**
 * Editorial Revenue Tracking Service
 * Tracks editorial-to-commerce conversions and revenue attribution
 */

// Track editorial commerce interactions
export const trackEditorialCommerce = (editorialId, productId, action, metadata = {}) => {
  try {
    // Log interaction for debugging
    console.log('📊 Editorial Commerce Tracking:', {
      editorialId,
      productId,
      action,
      metadata,
      timestamp: new Date().toISOString()
    });

    // Store interaction in localStorage for now (will move to Firebase later)
    const interactions = getEditorialInteractions();
    const interaction = {
      id: generateInteractionId(),
      editorialId,
      productId,
      action,
      metadata,
      timestamp: Date.now()
    };

    interactions.push(interaction);
    localStorage.setItem('editorialInteractions', JSON.stringify(interactions));

    // Update editorial-specific metrics
    updateEditorialMetrics(editorialId, action, productId, metadata);

    return interaction;
  } catch (error) {
    console.error('❌ Error tracking editorial commerce:', error);
    return null;
  }
};

// Get all editorial interactions
export const getEditorialInteractions = () => {
  try {
    const stored = localStorage.getItem('editorialInteractions');
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('❌ Error getting editorial interactions:', error);
    return [];
  }
};

// Get interactions for specific editorial
export const getEditorialInteractionsByEditorial = (editorialId) => {
  const interactions = getEditorialInteractions();
  return interactions.filter(interaction => interaction.editorialId === editorialId);
};

// Calculate editorial performance metrics
export const calculateEditorialMetrics = (editorialId) => {
  const interactions = getEditorialInteractionsByEditorial(editorialId);
  
  const metrics = {
    totalInteractions: interactions.length,
    saves: interactions.filter(i => i.action === 'save').length,
    shares: interactions.filter(i => i.action === 'share').length,
    readMore: interactions.filter(i => i.action === 'read_more').length,
    shopNavigation: interactions.filter(i => i.action === 'shop_navigation').length,
    productClicks: interactions.filter(i => i.action === 'product_mention_click' || i.action === 'related_product_click').length,
    uniqueUsers: new Set(interactions.map(i => i.metadata?.userId)).size,
    
    // Commerce-specific metrics
    commerceInteractions: interactions.filter(i => 
      i.action.includes('product') || i.action.includes('shop')
    ).length,
    
    // Conversion funnel
    conversionRate: 0, // Will calculate based on shop visits vs purchases
    
    // Time-based analysis
    interactionsByHour: {},
    interactionsByDay: {},
    
    // Most clicked products
    topProducts: {},
    
    // Revenue attribution (placeholder for now)
    attributedRevenue: 0,
    estimatedCommissions: 0
  };

  // Calculate time-based metrics
  interactions.forEach(interaction => {
    const date = new Date(interaction.timestamp);
    const hour = date.getHours();
    const day = date.toDateString();
    
    metrics.interactionsByHour[hour] = (metrics.interactionsByHour[hour] || 0) + 1;
    metrics.interactionsByDay[day] = (metrics.interactionsByDay[day] || 0) + 1;
    
    // Track product popularity
    if (interaction.productId) {
      metrics.topProducts[interaction.productId] = (metrics.topProducts[interaction.productId] || 0) + 1;
    }
  });

  // Calculate conversion rate
  const shopVisits = metrics.shopNavigation + metrics.productClicks;
  if (shopVisits > 0) {
    // This is a simplified calculation - in reality we'd track actual purchases
    metrics.conversionRate = (shopVisits * 0.15); // Assume 15% conversion rate
  }

  return metrics;
};

// Get aggregated metrics for all editorials
export const getAllEditorialMetrics = () => {
  const interactions = getEditorialInteractions();
  const editorialIds = [...new Set(interactions.map(i => i.editorialId))];
  
  const allMetrics = {};
  editorialIds.forEach(editorialId => {
    allMetrics[editorialId] = calculateEditorialMetrics(editorialId);
  });

  return allMetrics;
};

// Calculate revenue attribution for editorial
export const calculateEditorialRevenue = (editorialId, editorialData) => {
  const metrics = calculateEditorialMetrics(editorialId);
  
  // Estimate revenue based on interactions and editorial commerce integration
  let estimatedRevenue = 0;
  
  if (editorialData?.commerceIntegration?.expectedRevenue) {
    const expectedRevenue = editorialData.commerceIntegration.expectedRevenue;
    
    // Calculate based on interaction conversion rates
    const shopInteractions = metrics.shopNavigation + metrics.productClicks;
    const conversionMultiplier = shopInteractions * 0.1; // 10% of interactions convert
    
    estimatedRevenue = {
      affiliate: expectedRevenue.affiliate * conversionMultiplier,
      sponsorship: expectedRevenue.sponsorship, // Flat fee regardless of performance
      total: (expectedRevenue.affiliate * conversionMultiplier) + expectedRevenue.sponsorship
    };
  }

  return {
    ...metrics,
    estimatedRevenue,
    revenuePerInteraction: estimatedRevenue.total / Math.max(metrics.totalInteractions, 1),
    revenuePerUser: estimatedRevenue.total / Math.max(metrics.uniqueUsers, 1)
  };
};

// Update editorial-specific metrics in localStorage
const updateEditorialMetrics = (editorialId, action, productId, metadata = {}) => {
  try {
    const metricsKey = `editorialMetrics_${editorialId}`;
    const stored = localStorage.getItem(metricsKey);
    const metrics = stored ? JSON.parse(stored) : {
      clicks: 0,
      conversions: 0,
      revenue: 0,
      lastUpdated: new Date().toISOString(),
      productInteractions: {}
    };

    // Update based on action
    switch (action) {
      case 'shop_navigation':
      case 'product_mention_click':
      case 'related_product_click':
        metrics.clicks += 1;
        if (productId) {
          metrics.productInteractions[productId] = (metrics.productInteractions[productId] || 0) + 1;
        }
        break;
      
      case 'purchase': // Will be implemented when we have actual purchase tracking
        metrics.conversions += 1;
        metrics.revenue += (metadata?.purchaseAmount || 0);
        break;
      
      default:
        // Handle other actions without error
        break;
    }

    metrics.lastUpdated = new Date().toISOString();
    localStorage.setItem(metricsKey, JSON.stringify(metrics));
  } catch (error) {
    console.error('❌ Error updating editorial metrics:', error);
  }
};

// Generate unique interaction ID
const generateInteractionId = () => {
  return `int_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Export top-performing editorials
export const getTopPerformingEditorials = (editorialData, limit = 5) => {
  const allMetrics = getAllEditorialMetrics();
  
  const editorialsWithMetrics = editorialData.map(editorial => {
    const metrics = allMetrics[editorial.id] || {};
    const revenue = calculateEditorialRevenue(editorial.id, editorial);
    
    return {
      ...editorial,
      metrics,
      performance: {
        totalInteractions: metrics.totalInteractions || 0,
        commerceInteractions: metrics.commerceInteractions || 0,
        estimatedRevenue: revenue.estimatedRevenue?.total || 0,
        engagementRate: metrics.totalInteractions / Math.max(metrics.uniqueUsers, 1),
        conversionRate: metrics.conversionRate || 0
      }
    };
  });

  // Sort by a composite performance score
  editorialsWithMetrics.sort((a, b) => {
    const scoreA = (a.performance.totalInteractions * 0.3) + 
                   (a.performance.commerceInteractions * 0.4) + 
                   (a.performance.estimatedRevenue * 0.3);
    const scoreB = (b.performance.totalInteractions * 0.3) + 
                   (b.performance.commerceInteractions * 0.4) + 
                   (b.performance.estimatedRevenue * 0.3);
    return scoreB - scoreA;
  });

  return editorialsWithMetrics.slice(0, limit);
};

// Clear all tracking data (for testing/reset)
export const clearEditorialTracking = () => {
  try {
    localStorage.removeItem('editorialInteractions');
    
    // Clear individual editorial metrics
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('editorialMetrics_')) {
        localStorage.removeItem(key);
      }
    });
    
    console.log('✅ Editorial tracking data cleared');
  } catch (error) {
    console.error('❌ Error clearing editorial tracking:', error);
  }
};

// Debug function to log current tracking state
export const debugEditorialTracking = () => {
  const interactions = getEditorialInteractions();
  const allMetrics = getAllEditorialMetrics();
  
  console.group('🔍 Editorial Tracking Debug');
  console.log('Total interactions:', interactions.length);
  console.log('Interactions by editorial:', allMetrics);
  console.log('Recent interactions:', interactions.slice(-10));
  console.groupEnd();
  
  return { interactions, allMetrics };
};

// Export for use in admin dashboard
export const generateEditorialRevenueReport = (editorialData) => {
  const allMetrics = getAllEditorialMetrics();
  
  const report = {
    summary: {
      totalEditorials: editorialData.length,
      totalInteractions: 0,
      totalCommerceInteractions: 0,
      totalEstimatedRevenue: 0,
      averageEngagementRate: 0
    },
    byEditorial: [],
    topPerforming: getTopPerformingEditorials(editorialData, 10),
    revenueBreakdown: {
      affiliate: 0,
      sponsorship: 0,
      total: 0
    }
  };

  editorialData.forEach(editorial => {
    const metrics = allMetrics[editorial.id] || {};
    const revenue = calculateEditorialRevenue(editorial.id, editorial);
    
    report.summary.totalInteractions += metrics.totalInteractions || 0;
    report.summary.totalCommerceInteractions += metrics.commerceInteractions || 0;
    report.summary.totalEstimatedRevenue += revenue.estimatedRevenue?.total || 0;
    
    if (revenue.estimatedRevenue) {
      report.revenueBreakdown.affiliate += revenue.estimatedRevenue.affiliate || 0;
      report.revenueBreakdown.sponsorship += revenue.estimatedRevenue.sponsorship || 0;
      report.revenueBreakdown.total += revenue.estimatedRevenue.total || 0;
    }
    
    report.byEditorial.push({
      editorial,
      metrics,
      revenue: revenue.estimatedRevenue
    });
  });

  // Calculate average engagement rate
  const editorialsWithInteractions = report.byEditorial.filter(e => e.metrics.totalInteractions > 0);
  if (editorialsWithInteractions.length > 0) {
    const totalEngagement = editorialsWithInteractions.reduce((sum, e) => 
      sum + (e.metrics.totalInteractions / Math.max(e.metrics.uniqueUsers, 1)), 0
    );
    report.summary.averageEngagementRate = totalEngagement / editorialsWithInteractions.length;
  }

  return report;
};